import { z } from 'zod';

/**
 * The capability keys the backend's `STAFF_CAPABILITIES` whitelist defines.
 *
 * Kept as a list for LABELLING and for the panel's own gates — never for
 * validating the map that comes back. The server answers every key it knows,
 * `false` included, and a key it adds tomorrow has to arrive intact rather than
 * fail the parse of the object that decides what the whole panel renders.
 */
export const STAFF_CAPABILITIES = [
  'REVIEW_QUESTION',
  'APPROVE_QUESTION',
  'CANCEL_TOURNAMENT',
  'MANAGE_USERS',
  'ADJUST_KOINS',
  'RECEIVE_ALERTS',
  'EXPORT_PERSONAL_DATA',
  'MOVE_ESCROW',
  // ko-backend#842 — the section reads that used to be ADMIN-only.
  'MANAGE_TOURNAMENTS',
  'REVIEW_LISTINGS',
  'VIEW_ANALYTICS',
  // ko-backend#1057 — MANAGE_USERS split in three: disputes, orders and trades
  // under review go to MANAGE_DISPUTES, the payouts list to VIEW_PAYOUTS.
  'MANAGE_DISPUTES',
  'VIEW_PAYOUTS',
  // KO Hub — the case queue, the office forms and the role «Operación KO Hub».
  'MANAGE_KOHUB',
] as const;
export type StaffCapability = (typeof STAFF_CAPABILITIES)[number];

/**
 * `GET /admin/me/capabilities` — what the CALLER may do, asked about themselves.
 *
 * Every field here is deliberately loose. This one object decides what the
 * panel paints, so a shape stricter than the server would not degrade a
 * section: it would blank the navigation for everybody the moment a new tier
 * level, named role or capability key shipped. The backend is the permission
 * boundary either way.
 *
 * `source` is the field that carries the most weight:
 *
 *   - `ADMIN` — the caller is an ADMIN, which every capability guard lets
 *     past (ko-backend#842); every key is `true`.
 *   - `STAFF_PROFILE`  — the map is somebody's decision. A `false` means no.
 *   - `NO_STAFF_PROFILE` — a STAFF member with no profile, whom every
 *     capability route refuses. See `auth/capabilities.ts`.
 */
export const MyCapabilities = z
  .object({
    userId: z.string(),
    /** Absent rather than null when the token carried no role claim. */
    role: z.string().nullable().optional(),
    tierLevel: z.string().nullable().optional(),
    capabilities: z.record(z.string(), z.boolean()).default({}),
    namedRoles: z.array(z.string()).default([]),
    rootAdmin: z.boolean().default(false),
    source: z.string().optional(),
  })
  .passthrough();
export type MyCapabilities = z.infer<typeof MyCapabilities>;
